// frontend/src/pages/purchase/PurchaseOrderCreate.jsx

import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select'; // 🌟 すべての選択項目をreact-selectで一元管理
import { getSuppliers } from '../../utils/supplierApi';
import {
  createPurchaseOrder,
  searchProductsForModal,
  getNextPoNumber,
  getActiveTaxRate
} from '../../utils/purchaseOrderApi';

const PurchaseOrderCreate = () => {
  const [suppliers, setSuppliers] = useState([]);

  // 親伝票のステート
  const [poData, setPoData] = useState({
    supplier_uuid: '',
    order_number: '',
    supplier_order_number: '',
    order_date: new Date().toISOString().slice(0, 10),
    delivery_date: new Date().toISOString().slice(0, 10),
    status: 'completed', // デフォルト: 入庫完了
    note: ''
  });

  const [items, setItems] = useState([]);
  const [taxRate, setTaxRate] = useState(0.10);

  // モーダル（商品検索画面）用のステート
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [modalSearchResults, setModalSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [draftVariants, setDraftVariants] = useState({});

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchSuppliers();
    fetchNextNumber();
    fetchTaxRate();
  }, []);

  const fetchTaxRate = async () => {
    try {
      const rate = await getActiveTaxRate('STANDARD');
      setTaxRate(rate);
    } catch (error) {
      console.error('税率の動的取得に失敗しました。10%で代替します。', error);
    }
  };

  const fetchNextNumber = async () => {
    try {
      const nextNum = await getNextPoNumber();
      setPoData(prev => ({ ...prev, order_number: nextNum }));
    } catch (error) {
      console.error('連番の取得に失敗しました', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('仕入先の取得に失敗しました', error);
    }
  };

  // 🌟 仕入先用の react-select 選択肢
  const supplierOptions = suppliers.map(s => ({
    value: s.uuid,
    label: `[${s.supplier_code || '--'}] ${s.name}`
  }));

  // 🌟 【新規】ステータス用の react-select 選択肢を定義
  const statusOptions = [
    { value: 'draft', label: '📝 下書き' },
    { value: 'ordered', label: '🚀 発注済' },
    { value: 'completed', label: '✅ 入庫完了' }
  ];

  const handlePoChange = (e) => {
    const { name, value } = e.target;
    setPoData({ ...poData, [name]: value });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const price = parseFloat(newItems[index].unit_price) || 0;
      newItems[index].subtotal = qty * price;
    }
    setItems(newItems);
  };

  const removeItemRow = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const subtotalAmount = items.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);
  const taxAmount = Math.floor(subtotalAmount * taxRate);
  const totalAmount = subtotalAmount + taxAmount;

  const openModal = () => {
    if (!poData.supplier_uuid) {
      alert('先に仕入先を選択してください。');
      return;
    }
    setIsModalOpen(true);
    setModalSearchQuery('');
    setModalSearchResults([]);
    setSelectedProduct(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleModalSearchChange = (e) => {
    const query = e.target.value;
    setModalSearchQuery(query);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (query.length >= 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        const results = await searchProductsForModal(query, poData.supplier_uuid);
        setModalSearchResults(results);
      }, 500);
    } else {
      setModalSearchResults([]);
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    const initialDrafts = {};
    product.variants.forEach(v => {
      const cost = v.default_cost_price != null ? Number(v.default_cost_price) : 0;
      initialDrafts[v.master_variant_uuid] = {
        quantity: '',
        unit_price: cost
      };
    });
    setDraftVariants(initialDrafts);
  };

  const handleDraftChange = (variantUuid, field, value) => {
    setDraftVariants(prev => ({
      ...prev,
      [variantUuid]: { ...prev[variantUuid], [field]: value }
    }));
  };

  const addDraftsToItems = () => {
    const newRows = [];

    selectedProduct.variants.forEach(v => {
      const draft = draftVariants[v.master_variant_uuid];
      const qty = parseInt(draft.quantity, 10);

      if (qty > 0) {
        const price = parseFloat(draft.unit_price) || 0;
        newRows.push({
          master_variant_uuid: v.master_variant_uuid,
          display_name: `${selectedProduct.name} - ${v.color_name || '-'} / ${v.size_name || '-'} [SKU: ${v.sku_code}]`,
          quantity: qty,
          unit_price: price,
          subtotal: qty * price,
          supplier_item_code: v.supplier_item_code || ''
        });
      }
    });

    if (newRows.length === 0) {
      alert('追加する商品の数量を1以上入力してください。');
      return;
    }

    setItems([...items, ...newRows]);
    closeModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!poData.supplier_uuid) return alert('仕入先を選択してください。');
    if (items.length === 0) return alert('明細が1件もありません。商品を追加してください。');

    const payload = {
      supplier_uuid: poData.supplier_uuid,
      order_number: poData.order_number,
      supplier_order_number: poData.supplier_order_number || null,
      order_date: poData.order_date,
      delivery_date: poData.delivery_date || null,
      status: poData.status,
      note: poData.note || null,
      subtotal_amount: subtotalAmount,
      tax_amount: taxAmount,
      tax_rate: taxRate,
      total_amount: totalAmount,
      items: items.map(item => ({
        master_variant_uuid: item.master_variant_uuid,
        quantity: parseInt(item.quantity, 10),
        unit_price: parseFloat(item.unit_price),
        subtotal: parseFloat(item.subtotal),
        supplier_item_code: item.supplier_item_code || null
      }))
    };

    try {
      await createPurchaseOrder(payload);
      alert('仕入伝票を作成し、在庫とカタログを更新しました！');
      window.location.href = '/purchase/purchase-orders';
    } catch (error) {
      console.error(error);
      alert('伝票の作成に失敗しました。');
    }
  };

  return (
    <div className="position-relative">
      <h1 className="page-header">仕入伝票の入力 <small>Enter Purchase Order</small></h1>

      <form onSubmit={handleSubmit}>
        <div className="panel panel-inverse">
          <div className="panel-heading">
            <h4 className="panel-title">伝票情報</h4>
          </div>
          <div className="panel-body">

            {/* === 1. 基本情報 === */}
            <div className="bg-light p-3 rounded mb-4">
              <div className="row mb-3">

                {/* 仕入先 */}
                <div className="col-md-4">
                  <label className="form-label text-dark fw-bold">仕入先 <span className="text-danger">*</span></label>
                  <Select
                    options={supplierOptions}
                    value={supplierOptions.find(o => o.value === poData.supplier_uuid) || null}
                    onChange={(opt) => setPoData({ ...poData, supplier_uuid: opt ? opt.value : '' })}
                    placeholder="-- 仕入先を検索して選択 --"
                    noOptionsMessage={() => "仕入先が見つかりません"}
                    isSearchable={true}
                    isClearable={true}
                    className="fw-bold"
                  />
                  <input type="hidden" name="supplier_uuid" value={poData.supplier_uuid} required />
                </div>

                {/* 自社伝票番号 */}
                <div className="col-md-4">
                  <label className="form-label text-dark fw-bold">自社伝票番号 <span className="text-danger">*</span></label>
                  <input type="text" className="form-control fw-bold text-primary" name="order_number" value={poData.order_number} onChange={handlePoChange} required />
                </div>

                {/* メーカー番号 */}
                <div className="col-md-4">
                  <label className="form-label text-dark fw-bold">メーカー番号 (メーカーの伝票番号)</label>
                  <input type="text" className="form-control" name="supplier_order_number" value={poData.supplier_order_number} onChange={handlePoChange} placeholder="任意（納品書の番号など）" />
                </div>
              </div>

              <div className="row">
                {/* 発注日 */}
                <div className="col-md-3">
                  <label className="form-label text-dark fw-bold">発注日 <span className="text-danger">*</span></label>
                  <input type="date" className="form-control" name="order_date" value={poData.order_date} onChange={handlePoChange} required />
                </div>

                {/* 入荷日 */}
                <div className="col-md-3">
                  <label className="form-label text-dark fw-bold">入荷日 (入庫完了日)</label>
                  <input type="date" className="form-control" name="delivery_date" value={poData.delivery_date} onChange={handlePoChange} />
                </div>

                {/* 伝票登録日 */}
                <div className="col-md-3">
                  <label className="form-label text-muted fw-bold">伝票登録日 (システム自動記録)</label>
                  <input type="date" className="form-control bg-light text-muted" value={new Date().toISOString().slice(0, 10)} disabled title="保存時に今日の日時が自動記録されます" />
                </div>

                {/* 🌟 ステータス (react-select に超絶アップデート！) */}
                <div className="col-md-3">
                  <label className="form-label text-dark fw-bold">ステータス <span className="text-danger">*</span></label>
                  <Select
                    options={statusOptions}
                    value={statusOptions.find(o => o.value === poData.status) || null}
                    onChange={(opt) => setPoData({ ...poData, status: opt ? opt.value : 'draft' })}
                    placeholder="-- ステータス --"
                    isSearchable={false} // ステータスは3択なので検索窓はオフにしてスッキリ
                    className="fw-bold"
                  />
                </div>
              </div>
            </div>

            {/* === 2. 明細一覧 === */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0"><i className="fa fa-box"></i> 入庫明細</h5>
              <button type="button" className="btn btn-primary btn-lg shadow-sm" onClick={openModal}>
                <i className="fa fa-search-plus me-2"></i> 商品を検索して追加
              </button>
            </div>

            <div className="table-responsive mb-4">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th width="15%">メーカー番号</th>
                    <th width="35%">商品名 / SKU</th>
                    <th width="15%" className="text-center">金額 (¥)</th>
                    <th width="15%" className="text-center">数量</th>
                    <th width="10%" className="text-end">小計 (¥)</th>
                    <th width="10%" className="text-center">削除</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        右上の「商品を検索して追加」ボタンから明細を追加してください。
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <input type="text" className="form-control" placeholder="未設定"
                            value={item.supplier_item_code || ''}
                            onChange={(e) => handleItemChange(index, 'supplier_item_code', e.target.value)} />
                        </td>
                        <td>
                          <div className="fw-bold text-success">{item.display_name}</div>
                        </td>
                        <td>
                          <input type="number" className="form-control text-end" min="0" step="0.01"
                            value={item.unit_price} onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)} required />
                        </td>
                        <td>
                          <div className="input-group">
                            <input type="number" className="form-control text-center" min="1"
                              value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} required />
                            <span className="input-group-text">個</span>
                          </div>
                        </td>
                        <td className="text-end bg-light fw-bold fs-6">
                          {item.subtotal.toLocaleString()}
                        </td>
                        <td className="text-center">
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeItemRow(index)}>
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* 計算表示パネル */}
            <div className="d-flex justify-content-end mb-4">
              <div className="text-end border border-2 border-primary p-3 rounded bg-light" style={{ minWidth: '300px' }}>
                <div className="d-flex justify-content-between mb-2 fs-6">
                  <span className="text-muted fw-bold">小計:</span>
                  <span>¥{subtotalAmount.toLocaleString()}</span>
                </div>
                <div className="d-flex justify-content-between mb-2 fs-6">
                  <span className="text-muted fw-bold">消費税 ({taxRate * 100}%):</span>
                  <span>¥{taxAmount.toLocaleString()}</span>
                </div>
                <hr className="my-2 border-secondary" />
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <span className="fw-bold fs-5">合計金額:</span>
                  <span className="text-primary fw-bold fs-3 ms-3">¥{totalAmount.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <hr />
            <div className="form-group mt-3">
              <label className="form-label fw-bold">備考メモ</label>
              <textarea className="form-control" name="note" rows="3" value={poData.note} onChange={handlePoChange}></textarea>
            </div>

          </div>
          <div className="panel-footer text-end p-3 bg-light">
            <button type="button" className="btn btn-default me-2 px-4" onClick={() => window.history.back()}>キャンセル</button>
            <button type="submit" className="btn btn-success btn-lg px-5"><i className="fa fa-save me-2"></i>伝票を保存して入庫する</button>
          </div>
        </div>
      </form>

      {/* 商品検索モーダル */}
      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="bg-white rounded shadow-lg overflow-hidden" style={{ width: '90%', maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

            <div className="d-flex justify-content-between align-items-center p-3 bg-dark text-white">
              <h5 className="mb-0">{selectedProduct ? 'SKUの数量を入力' : '商品検索'}</h5>
              <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
            </div>

            <div className="p-4 overflow-auto" style={{ flex: 1 }}>

              {!selectedProduct ? (
                <>
                  <div className="input-group mb-4">
                    <span className="input-group-text bg-white"><i className="fa fa-search text-muted"></i></span>
                    <input type="text" className="form-control form-control-lg border-start-0"
                      placeholder="品番（自社/仕入先）を入力..."
                      value={modalSearchQuery} onChange={handleModalSearchChange} autoFocus
                    />
                  </div>

                  <div className="row g-3">
                    {modalSearchResults.length === 0 && modalSearchQuery.length >= 2 && (
                      <div className="text-center text-muted mt-4">該当する商品が見つかりません。</div>
                    )}
                    {modalSearchResults.map(product => (
                      <div className="col-md-6 col-lg-4" key={product.master_product_uuid}>
                        <div className="card h-100 border-primary cursor-pointer shadow-sm hover-effect" onClick={() => handleSelectProduct(product)} style={{ cursor: 'pointer' }}>
                          <div className="d-flex align-items-center p-2 border-bottom">
                            {product.image_url ? (
                              <img src={product.image_url} alt={product.name} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
                            ) : (
                              <div className="bg-light text-muted d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px', borderRadius: '4px' }}>
                                <i className="fa fa-image fs-4"></i>
                              </div>
                            )}
                            <div className="ms-3 flex-grow-1 min-w-0">
                              <h6 className="mb-1" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', fontSize: '0.95rem', lineHeight: '1.4' }} title={product.name}>
                                {product.name || '商品名未設定'}
                              </h6>
                              <div className="small text-muted">品番: {product.model_code}</div>
                            </div>
                          </div>
                          <div className="card-body py-2 px-3 bg-light d-flex justify-content-between align-items-center">
                            <span className="small text-muted">{product.variants.length} 種類のSKU</span>
                            <button className="btn btn-sm btn-primary rounded-pill px-3">選択</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (

                <>
                  <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                    {selectedProduct.image_url ? (
                      <img src={selectedProduct.image_url} alt={selectedProduct.name} className="rounded shadow-sm me-3" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                    ) : (
                      <div className="bg-light rounded shadow-sm d-flex align-items-center justify-content-center me-3" style={{ width: '80px', height: '80px' }}>
                        <i className="fa fa-image fs-3 text-muted"></i>
                      </div>
                    )}
                    <div>
                      <h4 className="mb-1">{selectedProduct.name}</h4>
                      <div className="text-muted">品番: {selectedProduct.model_code}</div>
                    </div>
                  </div>

                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>カラー / サイズ (SKU)</th>
                        <th width="20%">仕入単価 (¥)</th>
                        <th width="25%">追加する数量</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProduct.variants.map(v => (
                        <tr key={v.master_variant_uuid}>
                          <td>
                            <div className="fw-bold">{v.color_name || '-'} / {v.size_name || '-'}</div>
                            <div className="small text-muted">{v.sku_code} {v.supplier_item_code && <span className="ms-2 text-primary">(メーカー品番: {v.supplier_item_code})</span>}</div>
                            {v.default_cost_price <= 0 && <span className="badge bg-warning text-dark mt-1">初登録または原価未設定</span>}
                          </td>
                          <td>
                            <input type="number" className="form-control text-end" min="0" step="0.01"
                              value={draftVariants[v.master_variant_uuid]?.unit_price || ''}
                              onChange={(e) => handleDraftChange(v.master_variant_uuid, 'unit_price', e.target.value)}
                            />
                          </td>
                          <td>
                            <div className="input-group">
                              <input type="number" className="form-control text-center fw-bold" placeholder="0" min="0"
                                value={draftVariants[v.master_variant_uuid]?.quantity || ''}
                                onChange={(e) => handleDraftChange(v.master_variant_uuid, 'quantity', e.target.value)}
                              />
                              <span className="input-group-text">個</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>

            <div className="p-3 bg-light border-top d-flex justify-content-between">
              {selectedProduct ? (
                <>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setSelectedProduct(null)}>
                    <i className="fa fa-arrow-left me-1"></i> 検索に戻る
                  </button>
                  <button type="button" className="btn btn-success px-4" onClick={addDraftsToItems}>
                    <i className="fa fa-check me-1"></i> 数量を入れたSKUを明細に追加
                  </button>
                </>
              ) : (
                <div className="w-100 text-end">
                  <button type="button" className="btn btn-secondary px-4" onClick={closeModal}>閉じる</button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default PurchaseOrderCreate;